/**
 * Axios Instance Configuration for React + Drupal Backend
 * 
 * Features:
 * - Strict TypeScript typings
 * - Auto-detection of backend environment
 * - Environment variable validation
 * - CORS support with credentials
 * - Azure AD / SSO cookie support
 * - Proper baseURL override for localhost ports (5000, 8000, 5173)
 * - Default URL: https://ai-hub-portal.ddev.site (local development)
 * 
 * @module axios.config
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_TIMEOUT } from '@core/constants/app.constants';
import { getApiBaseUrl as resolveApiBaseUrl } from '@core/utils/env.utils';

/**
 * Drupal backend URL (default for all API calls)
 * Local development: http://localhost:8000
 * Production: https://aiportal-d-enw-webapp01.azurewebsites.net (when needed)
 */

/**
 * Environment variable validation result
 */
interface EnvValidation {
    isValid: boolean;
    baseUrl: string;
    source: 'env' | 'default' | 'override';
    warnings: string[];
}

/**
 * Validates and processes the API base URL from environment variables
 * Priority: window._env_.AIPORTAL_APP_BASE_URL (FastAPI) > window._env_.AIPORTAL_API_BASE_URL (Vite)
 * 
 * Rules:
 * 1. Read from window._env_.AIPORTAL_APP_BASE_URL first (FastAPI runtime config)
 * 2. Fallback to window._env_.AIPORTAL_API_BASE_URL (Vite build-time config)
 * 3. If URL contains localhost ports (5000, 8000, 5173), override to production Drupal URL
 * 4. Remove trailing /api to prevent double /api/api in URLs
 * 5. Force HTTPS for production URLs (not localhost)
 * 6. Validate URL format
 * 
 * Default URL: http://localhost:8000
 * 
 * @returns Validated base URL configuration
 */
const getApiBaseUrl = (): EnvValidation => {
    const warnings: string[] = [];
    let baseUrl: string = resolveApiBaseUrl();
    let source: 'env' | 'default' | 'override' = baseUrl.includes('localhost') ? 'default' : 'env';


    // Step 1: Remove trailing /api to prevent double /api/api in URLs
    const cleanUrl = baseUrl.replace(/\/api\/?$/, '');
    if (cleanUrl !== baseUrl) {
        warnings.push(`Removed trailing /api from base URL: ${baseUrl} → ${cleanUrl}`);
        baseUrl = cleanUrl;
    }

    // Step 2: Force HTTPS for production URLs (not localhost)
    if (baseUrl.startsWith('http://') && !baseUrl.includes('localhost')) {
        const httpsUrl = baseUrl.replace('http://', 'https://');
        warnings.push(`Forced HTTPS for production URL: ${baseUrl} → ${httpsUrl}`);
        baseUrl = httpsUrl;
    }

    // Step 3: Validate URL format
    try {
        new URL(baseUrl);
    } catch (error) {
        warnings.push(`Invalid URL format: ${baseUrl}. Falling back to default.`);
        baseUrl = resolveApiBaseUrl();
        source = 'default';
    }

    // Log warnings in development
    if (import.meta.env.DEV && warnings.length > 0) {
        warnings.forEach((warning) => {
            console.warn(`⚠️ Axios Config: ${warning}`);
        });
    }

    // Log final base URL in development
    if (import.meta.env.DEV) {
        console.log(`🔧 Axios Base URL: ${baseUrl} (source: ${source})`);
    }

    return {
        isValid: true,
        baseUrl,
        source,
        warnings,
    };
};

export const getBaseUrl = (): string => resolveApiBaseUrl();

/**
 * Creates and configures the Axios instance for Drupal DDEV backend
 * 
 * Configuration:
 * - withCredentials: true (for CORS with cookies)
 * - Proper headers for JSON API
 * - Configurable timeout (default: 60s, configurable via API_TIMEOUT constant)
 * - Supports all HTTP methods (GET, POST, PUT, PATCH, DELETE)
 * 
 * @returns Configured Axios instance
 */
export const createAxiosInstance = (): AxiosInstance => {
    const { baseUrl } = getApiBaseUrl();

    const instance = axios.create({
        baseURL: baseUrl,
        timeout: API_TIMEOUT, // Configurable timeout (default: 60 seconds)
        withCredentials: true, // CRITICAL: Enable CORS with credentials for Azure AD / SSO cookies
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
        // Ensure proper handling of all HTTP methods
        validateStatus: (status) => {
            // Don't throw errors for status codes < 500
            // Let interceptors handle error responses
            return status < 500;
        },
    });

    if (import.meta.env.DEV) {
        console.log(`🔧 Axios timeout configured: ${API_TIMEOUT}ms (${API_TIMEOUT / 1000}s)`);
    }

    return instance;
};

/**
 * Global Axios instance (lazy initialization)
 * All services should import and use this instance
 * The instance is created on first access to ensure window._env_ is loaded
 * 
 * @example
 * ```typescript
 * import { axiosInstance } from '@core/config/axios.config';
 * 
 * const response = await axiosInstance.get('/api/tools/list');
 * ```
 */
let _axiosInstance: AxiosInstance | null = null;

const getAxiosInstance = (): AxiosInstance => {
    if (!_axiosInstance) {
        _axiosInstance = createAxiosInstance();
    }
    return _axiosInstance;
};

// Export as a getter-like object that creates instance on first access
export const axiosInstance = new Proxy({} as AxiosInstance, {
    get(_target, prop) {
        const instance = getAxiosInstance();
        const value = instance[prop as keyof AxiosInstance];
        if (typeof value === 'function') {
            return value.bind(instance);
        }
        return value;
    },
    set(_target, prop, value) {
        const instance = getAxiosInstance();
        (instance as any)[prop] = value;
        return true;
    },
}) as AxiosInstance;

/**
 * Type-safe Axios request config with Drupal-specific options
 */
export interface DrupalAxiosRequestConfig extends AxiosRequestConfig {
    /**
     * Skip retry for this request (useful for token refresh)
     */
    skipRetry?: boolean;

    /**
     * Skip error notification (useful for background requests)
     */
    skipErrorNotification?: boolean;

    /**
     * Skip error logging to console (useful for endpoints that gracefully handle errors)
     */
    skipErrorLogging?: boolean;

    /**
     * Skip proactive token refresh check (useful for refresh endpoint itself)
     */
    skipTokenRefresh?: boolean;

    /**
     * Internal flag to track if request has been retried (for token refresh)
     */
    _retry?: boolean;
}

/**
 * Type-safe Axios response for Drupal API
 */
export type DrupalAxiosResponse<T = any> = AxiosResponse<T> & {
    config: DrupalAxiosRequestConfig;
};

// Export types for use in services
export type { AxiosInstance, AxiosRequestConfig, AxiosResponse };
